import styled from "styled-components";
import { Row, Col, Form, Button, Input, FormFeedback, Label, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const RebelColors = {
    // Dark theme matching header/sidebar gradients
    background: "#1a1a1a",
    surface: "#2d2d2d",
    surfaceLight: "#323232",
    primary: "#ffffff",
    primaryMuted: "#b0b0b0",
    accent: "#4a9eff", // Blue accent
    accentSecondary: "#6c5ce7", // Purple accent
    success: "#00b894",
    warning: "#fdcb6e",
    danger: "#e17055",
    border: "#404040",
    borderLight: "#555555",
    gold: "#ffd700", // For premium R.E.B.E.L branding
    // Gradients matching header/sidebar
    gradientStart: "#000000", // Pure black like header
    gradientMid: "#323232",   // Mid-tone gray
    gradientEnd: "#1a1a1a"    // Dark gray
};

export const RebelContainer = styled.div`
    background: linear-gradient(135deg, ${RebelColors.gradientStart} 0%, ${RebelColors.gradientEnd} 100%);
    min-height: 100vh;
    color: ${RebelColors.primary};
    padding: 0;
    margin: 0;
`;

export const RebelContentWrapper = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
`;

export const RebelCard = styled.div`
    background: ${RebelColors.surface};
    border-radius: 12px;
    padding: 24px;
    margin: 24px 15px;
    border: 1px solid ${RebelColors.border};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

    p {
        color: ${RebelColors.primary};
    }

    label {
        color: ${RebelColors.primary};
    }

    a {
        color: ${RebelColors.gold};
    }
`;

export const RebelHeaderCard = styled(RebelCard)`
    background: linear-gradient(135deg, ${RebelColors.surface} 0%, ${RebelColors.surfaceLight} 100%);
    border: 2px solid ${RebelColors.gold};
    text-align: center;
    margin-bottom: 32px;
`;

export const RebelTitle = styled.h1`
    color: ${RebelColors.gold};
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 8px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    letter-spacing: 2px;
`;

export const RebelSubtitle = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 1.1rem;
    font-style: italic;
    margin-bottom: 0;
    letter-spacing: 1px;
`;

export const RebelSectionTitle = styled.h2`
    color: ${RebelColors.primary};
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 16px;
    border-bottom: 2px solid ${RebelColors.accent};
    padding-bottom: 8px;
`;

export const RebelForm = styled(Form)`
    background: ${RebelColors.surface};
    border-radius: 8px;
    padding: 20px;
    border: 1px solid ${RebelColors.border};
`;

export const RebelFormGroup = styled.div`
    margin-bottom: 20px;
`;

export const RebelLabel = styled(Label)`
    color: ${RebelColors.primary};
    font-weight: 500;
    margin-bottom: 8px;
    font-size: 0.95rem;
`;

export const RebelInput = styled(Input)`
    background-color: ${RebelColors.background};
    border: 1px solid ${RebelColors.border};
    color: ${RebelColors.primary};
    border-radius: 6px;
    padding: 12px 16px;
    transition: all 0.3s ease;

    &:focus {
        background-color: ${RebelColors.surface};
        border-color: ${RebelColors.accent};
        box-shadow: 0 0 0 3px rgba(74, 158, 255, 0.1);
        color: ${RebelColors.primary};
    }

    &::placeholder {
        color: ${RebelColors.primaryMuted};
    }

    &:hover {
        border-color: ${RebelColors.borderLight};
    }
`;

export const RebelTextarea = styled(RebelInput).attrs({ as: 'textarea' })`
    min-height: 100px;
    resize: vertical;
`;

export const RebelSelect = styled(RebelInput).attrs({ as: 'select' })`
    cursor: pointer;

    /* Ensure consistent borders for dropdown */
    &:focus {
        outline: none;
        border: 1px solid ${RebelColors.accent};
    }

    option {
        background-color: ${RebelColors.surface};
        color: ${RebelColors.primary};
        padding: 8px 12px;
        border: none;
    }
`;

export const RebelButton = styled(Button)`
    background: linear-gradient(135deg, ${RebelColors.gold} 0%, #d4af37 100%);
    border: none;
    border-radius: 6px;
    padding: 10px 24px;
    min-height: 46px;
    font-weight: 600;
    color: #000000;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    /* Make icons black on gold buttons */
    svg {
        color: #000000;
    }

    &:hover {
        background: linear-gradient(135deg, #d4af37 0%, ${RebelColors.gold} 100%);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(255, 215, 0, 0.4);
        color: #000000;
    }

    &:active {
        transform: translateY(0);
    }

    &:focus {
        box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.3);
        color: #000000;
    }
`;

export const RebelSecondaryButton = styled(RebelButton)`
    background: transparent;
    border: 1px solid ${RebelColors.border};
    color: ${RebelColors.primary};

    &:hover {
        background: ${RebelColors.surfaceLight};
        border-color: ${RebelColors.gold};
        color: ${RebelColors.primary};
        box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
    }
`;

export const RebelDangerButton = styled(RebelButton)`
    background: linear-gradient(135deg, ${RebelColors.danger} 0%, #d63031 100%);

    &:hover {
        background: linear-gradient(135deg, #d63031 0%, ${RebelColors.danger} 100%);
        box-shadow: 0 4px 12px rgba(225, 112, 85, 0.3);
    }
`;

export const RebelSuccessButton = styled(RebelButton)`
    background: linear-gradient(135deg, ${RebelColors.success} 0%, #00a085 100%);

    &:hover {
        background: linear-gradient(135deg, #00a085 0%, ${RebelColors.success} 100%);
        box-shadow: 0 4px 12px rgba(0, 184, 148, 0.3);
    }
`;

export const RebelIcon = styled(FontAwesomeIcon)`
    color: ${RebelColors.gold};
    margin-right: 8px;
    font-size: 1.2rem;
`;

export const RebelStatusBadge = styled.span`
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    ${props => {
        switch (props.status) {
            case 'active':
                return `
                    background: ${RebelColors.success};
                    color: white;
                `;
            case 'pending':
                return `
                    background: ${RebelColors.warning};
                    color: ${RebelColors.background};
                `;
            case 'inactive':
                return `
                    background: ${RebelColors.danger};
                    color: white;
                `;
            default:
                return `
                    background: ${RebelColors.primaryMuted};
                    color: ${RebelColors.background};
                `;
        }
    }}
`;

export const RebelPriceDisplay = styled.div`
    background: linear-gradient(135deg, ${RebelColors.gold}20 0%, ${RebelColors.accent}20 100%);
    border: 1px solid ${RebelColors.gold};
    border-radius: 8px;
    padding: 16px;
    text-align: center;
    margin: 16px 0;
`;

export const RebelPrice = styled.span`
    color: ${RebelColors.gold};
    font-size: 1.8rem;
    font-weight: 700;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

export const RebelPriceLabel = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 0.9rem;
    margin: 4px 0 0 0;
    text-transform: uppercase;
    letter-spacing: 1px;
`;

export const RebelGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    max-width: 100%;
    gap: 24px;
    margin: 24px 0;

    @media (min-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

export const RebelListItem = styled.div`
    background: ${RebelColors.surface};
    border: 1px solid ${RebelColors.border};
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
`;

export const RebelFormFeedback = styled(FormFeedback)`
    color: ${RebelColors.danger};
    font-size: 0.85rem;
    margin-top: 4px;
`;

export const RebelLoadingSpinner = styled.div`
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 2px solid ${RebelColors.borderLight};
    border-radius: 50%;
    border-top-color: ${RebelColors.accent};
    animation: spin 1s ease-in-out infinite;

    @keyframes spin {
        to { transform: rotate(360deg); }
    }
`;

export const RebelEmptyState = styled.div`
    text-align: center;
    padding: 48px 24px;
    color: ${RebelColors.primaryMuted};

    h3 {
        color: ${RebelColors.primary};
        margin-bottom: 16px;
    }

    p {
        font-size: 1.1rem;
        margin-bottom: 24px;
    }
`;

export const RebelSpacer = styled.div`
    height: ${props => props.height || '24px'};
`;

export const RebelRow = styled(Row)`
    margin: 0;
`;

export const RebelCol = styled(Col)`
    padding: 12px;
`;

export const RebelModal = styled(Modal)`
    .modal-content {
        background-color: ${RebelColors.surface};
        border: 1px solid ${RebelColors.border};
        border-radius: 12px;
        color: ${RebelColors.primary};
    }
`;

export const RebelModalHeader = styled(ModalHeader)`
    background-color: ${RebelColors.background};
    border-bottom: 1px solid ${RebelColors.border};
    color: ${RebelColors.primary};
    border-radius: 12px 12px 0 0;

    /* Bootstrap 4 uses .close class, not .btn-close */
    .close {
        color: #e17055 !important;
        text-shadow: none !important;
        opacity: 0.7 !important;
        transition: all 0.3s ease;
        font-weight: 700;

        &:hover {
            color: #d63031 !important;
            opacity: 1 !important;
        }

        &:focus {
            outline: none;
        }
    }

    /* Support for both Bootstrap 4 (.close) and Bootstrap 5 (.btn-close) */
    .btn-close {
        background: transparent !important;
        opacity: 0.7 !important;
        transition: all 0.3s ease;
        color: #e17055 !important;
        text-shadow: none !important;

        /* Create red X using background image */
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23e17055'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e") !important;

        &:hover {
            opacity: 1 !important;
            color: #d63031 !important;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23d63031'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e") !important;
        }
    }
`;

export const RebelModalBody = styled(ModalBody)`
    background-color: ${RebelColors.surface};
    color: ${RebelColors.primary};
    padding: 24px;
`;

export const RebelModalFooter = styled(ModalFooter)`
    background-color: ${RebelColors.background};
    border-top: 1px solid ${RebelColors.border};
    border-radius: 0 0 12px 12px;
    padding: 16px 24px;
`;